/**
 * @file Components/Sections/Features.jsx
 * @description Premium service highlights — Group Tours, Handpicked Hotels,
 * Authentic Experiences. Each card has a bg image, live stats, rich
 * descriptions, and a clear CTA.
 */

import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, Hotel, Sparkles, ArrowRight, Star, MapPin, CheckCircle } from "lucide-react";

const features = [
  {
    icon: Users,
    title: "Group Tours",
    subtitle: "Travel with like-minded adventurers",
    description: "Join curated group departures across India with expert guides, all-inclusive pricing, and a community of fellow explorers.",
    image: "https://images.unsplash.com/photo-1527631746610-bca00a040d60?w=600&q=80",
    link: "/tours?category=group",
    cta: "Browse Group Tours",
    stats: [
      { label: "Active Groups", value: "24+" },
      { label: "Avg Rating", value: "4.8" },
    ],
    highlights: ["Expert local guides", "All meals included", "Small groups (8-15)"],
    gradient: "from-blue-600 to-indigo-700",
    accent: "bg-blue-500",
  },
  {
    icon: Hotel,
    title: "Handpicked Hotels",
    subtitle: "Luxury stays, vetted by our team",
    description: "Every property is personally inspected — from boutique homestays in the hills to 5-star resorts by the beach. No compromises.",
    image: "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600&q=80",
    link: "/hotels",
    cta: "Search Hotels",
    stats: [
      { label: "Properties", value: "500+" },
      { label: "Cities", value: "45+" },
    ],
    highlights: ["Personally inspected", "Best rate guarantee", "Free cancellation"],
    gradient: "from-purple-600 to-pink-600",
    accent: "bg-purple-500",
  },
  {
    icon: Sparkles,
    title: "Authentic Experiences",
    subtitle: "Go beyond the tourist trail",
    description: "Cooking with locals, sunrise treks, hidden temples, backwater kayaking — experiences you won't find on any other platform.",
    image: "https://images.unsplash.com/photo-1504598318550-17eba1008a68?w=600&q=80",
    link: "/tours?category=customized",
    cta: "Discover Experiences",
    stats: [
      { label: "Experiences", value: "120+" },
      { label: "Regions", value: "18" },
    ],
    highlights: ["Off-the-beaten-path", "Local hosts & guides", "Unique to Nikaba"],
    gradient: "from-orange-500 to-red-600",
    accent: "bg-orange-500",
  },
];

const Features = () => {
  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="text-sm font-bold text-[#243185] uppercase tracking-wider">What We Offer</span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-3">
            Services That Set Us Apart
          </h2>
          <p className="text-gray-500 text-lg max-w-xl mx-auto">
            Every detail curated. Every experience unforgettable.
          </p>
        </div>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {features.map((feature, i) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.12 }}
              >
                <Link
                  to={feature.link}
                  className="group block bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-400 border border-gray-100 h-full"
                >
                  {/* Image Header */}
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={feature.image}
                      alt={feature.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      loading="lazy"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t ${feature.gradient} opacity-60 group-hover:opacity-70 transition-opacity`} />

                    {/* Icon Badge */}
                    <div className="absolute top-4 left-4 w-11 h-11 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/20">
                      <Icon size={20} className="text-white" />
                    </div>

                    {/* Stats on image */}
                    <div className="absolute bottom-4 left-4 right-4 flex items-center gap-3">
                      {feature.stats.map((stat, j) => (
                        <div key={j} className="bg-black/30 backdrop-blur-sm rounded-lg px-3 py-1.5 border border-white/10">
                          <p className="text-white font-black text-sm leading-none">{stat.value}</p>
                          <p className="text-white/60 text-[10px] mt-0.5">{stat.label}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    <h3 className="text-lg font-bold text-gray-900 group-hover:text-[#243185] transition-colors">
                      {feature.title}
                    </h3>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mt-0.5">
                      {feature.subtitle}
                    </p>
                    <p className="text-sm text-gray-500 mt-3 leading-relaxed">
                      {feature.description}
                    </p>

                    {/* Highlights */}
                    <div className="flex flex-wrap gap-x-3 gap-y-1.5 mt-4">
                      {feature.highlights.map((h, j) => (
                        <span key={j} className="flex items-center gap-1 text-[11px] text-gray-500 font-medium">
                          <CheckCircle size={11} className="text-emerald-500" /> {h}
                        </span>
                      ))}
                    </div>

                    {/* CTA */}
                    <div className="flex items-center gap-1.5 mt-5 text-[#243185] font-bold text-sm group-hover:gap-2.5 transition-all">
                      {feature.cta} <ArrowRight size={14} />
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Features;
